import { useCallback, useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import HardBreak from '@tiptap/extension-hard-break';
import { 
  Bold, 
  Italic, 
  Code2,
  Heading2, 
  Heading3, 
  List, 
  ListOrdered, 
  Link as LinkIcon, 
  Image as ImageIcon,
  Unlink,
  Loader2,
  Quote,
  Undo,
  Redo,
  Maximize2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { normalizeTiptapHtml } from '@/lib/richtext/normalizeTiptapHtml';
import { sanitizeRichTextHtml } from '@/lib/richtext/sanitizeRichTextHtml';
import { IframeEmbed } from '@/lib/richtext/IframeEmbed';
import { WritingSheet } from '@/components/editor';
import { track } from '@/lib/analytics';
import { useTrackOnce } from '@/lib/analytics/useTrackOnce';

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  /** When provided, enables image upload to producer-media bucket */
  producerId?: string;
  disabled?: boolean;
  placeholder?: string;
  /** "simple" strips heading, blockquote, image, undo/redo from toolbar */
  preset?: 'simple' | 'full';
  /** Override default min-height (px) */
  minHeightPx?: number;
  /** Enable expanded writing mode — adds a toolbar button to open a spacious overlay */
  expandable?: boolean;
  /** Title shown in expanded writing mode header */
  expandedTitle?: string;
  /** Description shown in expanded writing mode header */
  expandedDescription?: string;
  /** Analytics: surface identifier (e.g. 'producer_editor') */
  expandedSurface?: string;
  /** Analytics: field identifier (e.g. 'long_description') */
  expandedField?: string;
}

const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB

/**
 * Convert plain text to HTML preserving paragraph breaks and line breaks.
 * - Double newlines (blank line) → new <p>
 * - Single newline within a paragraph → <br/>
 */
function plainTextToHtml(text: string): string {
  // Split on blank lines (one or more empty lines)
  const paragraphs = text.split(/\n\s*\n/);
  return paragraphs
    .map((p) => {
      const trimmed = p.trim();
      if (!trimmed) return '';
      // Within a paragraph, convert single newlines to <br/>
      const inner = trimmed
        .split('\n')
        .map((line) => line.trimEnd())
        .join('<br/>');
      return `<p>${inner}</p>`;
    })
    .filter(Boolean)
    .join('');
}

export function RichTextEditor({
  value,
  onChange,
  producerId,
  disabled = false,
  placeholder = 'Skriv din beskrivning här...',
  preset = 'full',
  minHeightPx,
  expandable = false,
  expandedTitle = 'Redigera text',
  expandedDescription,
  expandedSurface = 'editor',
  expandedField = 'text',
}: RichTextEditorProps) {
  const { toast } = useToast();
  const [isUploading, setIsUploading] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkNewTab, setLinkNewTab] = useState(true);
  const [linkPopoverOpen, setLinkPopoverOpen] = useState(false);
  const [expandedOpen, setExpandedOpen] = useState(false);
  const [isHtmlMode, setIsHtmlMode] = useState(false);
  const [htmlSource, setHtmlSource] = useState(value || '');

  // ── Writing mode discoverability ──
  const storageKey = `farmly_wm_used_${expandedSurface}_${expandedField}`;
  const [showHint, setShowHint] = useState(() => {
    if (!expandable) return false;
    try { return localStorage.getItem(storageKey) !== '1'; } catch { return true; }
  });

  // Track hint shown once per mount
  useTrackOnce(
    'editor_writing_mode_hint_shown',
    { surface: expandedSurface, field: expandedField },
    expandable && showHint ? `${expandedSurface}_${expandedField}` : null,
  );

  const handleOpenWritingMode = useCallback(() => {
    setExpandedOpen(true);
    setShowHint(false);
    try { localStorage.setItem(storageKey, '1'); } catch {}
    track('editor_writing_mode_opened', { surface: expandedSurface, field: expandedField, source: 'button' });
  }, [storageKey, expandedSurface, expandedField]);

  const handleCloseWritingMode = useCallback((open: boolean) => {
    setExpandedOpen(open);
    if (!open) {
      track('editor_writing_mode_closed', { surface: expandedSurface, field: expandedField });
    }
  }, [expandedSurface, expandedField]);

  // Keep a ref to latest onChange to avoid re-creating editor
  const isSimple = preset === 'simple';
  const editorMinHeight = minHeightPx ?? (isSimple ? 140 : 200);

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: isSimple ? false : { levels: [2, 3] },
        blockquote: isSimple ? false : {},
        // Disable StarterKit's built-in hardBreak so we add our own
        hardBreak: false,
      }),
      HardBreak.configure({
        // Shift+Enter inserts <br>
        keepMarks: true,
      }),
      IframeEmbed,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-primary underline hover:text-primary/80',
        },
      }),
      ...(isSimple
        ? []
        : [
            Image.configure({
              HTMLAttributes: {
                class: 'max-w-full h-auto rounded-lg my-4',
              },
            }),
          ]),
      Placeholder.configure({
        placeholder,
      }),
    ],
    content: sanitizeRichTextHtml(value || ''),
    editable: !disabled,
    onUpdate: ({ editor: e }) => {
      const cleaned = sanitizeRichTextHtml(e.getHTML());
      setHtmlSource(cleaned);
      onChangeRef.current(cleaned);
    },
    onBlur: ({ editor: e }) => {
      const cleaned = sanitizeRichTextHtml(e.getHTML());
      setHtmlSource(cleaned);
      onChangeRef.current(cleaned);
      e.commands.setContent(cleaned, { emitUpdate: false });
    },
    editorProps: {
      attributes: {
        class: `farmly-richtext focus:outline-none`,
        style: `min-height:${editorMinHeight}px`,
      },
      handlePaste: (view, event) => {
        // If clipboard has HTML, let TipTap handle it natively
        console.log('Clipboard HTML:', event.clipboardData?.getData('text/html'));
        return false; // Disable custom paste handling for now

        const html = event.clipboardData?.getData('text/html');
        if (html && html.trim().length > 0) {
          return false;
        }

        // For plain text, convert newlines to proper paragraphs/breaks
        const text = event.clipboardData?.getData('text/plain');
        if (!text) return false;

        const converted = plainTextToHtml(text);
        if (!converted) return false;

        // Use the editor instance from the view's state
        const { state, dispatch } = view;
        // We need to use the editor commands via the editor ref
        // but handlePaste doesn't receive the editor directly.
        // Instead, insert as HTML via the transaction system.
        // The simplest safe approach: prevent default, then use
        // the editor (captured via closure) to insert content.
        event.preventDefault();

        // Access the editor from the EditorView's state
        // TipTap stores the editor on the view
        const tiptapEditor = (view as any).editor;
        if (tiptapEditor) {
          tiptapEditor.commands.insertContent(converted, {
            parseOptions: { preserveWhitespace: false },
          });
        }

        return true;
      },
    },
  });

  // Sync external value changes (form reset, initial load) without render loops
  useEffect(() => {
    if (isHtmlMode) return;
    if (!editor) return;
    if (editor.isFocused) return;

    const currentHtml = sanitizeRichTextHtml(editor.getHTML());
    const incomingHtml = sanitizeRichTextHtml(value || '');

    if (currentHtml === incomingHtml) return;

    setHtmlSource(value || '');
    editor.commands.setContent(incomingHtml, { emitUpdate: false });
  }, [editor, value, isHtmlMode]);

  useEffect(() => {
    if (isHtmlMode) return;
    setHtmlSource(value || '');
  }, [value, isHtmlMode]);

  const handleImageUpload = useCallback(async () => {
    if (!producerId) return;

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;

      if (file.size > MAX_IMAGE_SIZE) {
        toast({
          title: 'Bilden är för stor',
          description: 'Max storlek är 5 MB.',
          variant: 'destructive',
        });
        return;
      }

      if (!file.type.startsWith('image/')) {
        toast({
          title: 'Ogiltig filtyp',
          description: 'Endast bilder är tillåtna.',
          variant: 'destructive',
        });
        return;
      }

      setIsUploading(true);

      try {
        const { compressImageFile } = await import('@/lib/media/compressImage');
        const compressed = await compressImageFile(file);
        const timestamp = Date.now();
        const ext = compressed.name.split('.').pop() || 'jpg';
        const filePath = `producers/${producerId}/${timestamp}-${crypto.randomUUID()}.${ext}`;

        const { error: uploadError } = await supabase.storage
          .from('producer-media')
          .upload(filePath, compressed, {
            cacheControl: '3600',
            upsert: false,
          });

        if (uploadError) {
          throw uploadError;
        }

        const { data: urlData } = supabase.storage
          .from('producer-media')
          .getPublicUrl(filePath);

        if (urlData?.publicUrl && editor) {
          editor.chain().focus().setImage({ src: urlData.publicUrl }).run();
          toast({
            title: 'Bild uppladdad',
            description: 'Bilden har lagts till i beskrivningen.',
          });
        }
      } catch (error) {
        console.error('Image upload error:', error);
        toast({
          title: 'Uppladdning misslyckades',
          description: 'Kunde inte ladda upp bilden. Försök igen.',
          variant: 'destructive',
        });
      } finally {
        setIsUploading(false);
      }
    };

    input.click();
  }, [editor, producerId, toast]);

  const handleSetLink = useCallback(() => {
    if (!editor) return;

    if (!linkUrl) {
      editor.chain().focus().unsetLink().run();
    } else {
      let url = linkUrl;
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        url = 'https://' + url;
      }
      
      editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ 
          href: url, 
          target: linkNewTab ? '_blank' : null,
        })
        .run();
    }

    setLinkUrl('');
    setLinkPopoverOpen(false);
  }, [editor, linkUrl, linkNewTab]);

  const handleOpenLinkPopover = useCallback(() => {
    if (!editor) return;
    
    const previousUrl = editor.getAttributes('link').href || '';
    setLinkUrl(previousUrl);
    setLinkPopoverOpen(true);
  }, [editor]);

  if (!editor) {
    return null;
  }

  const handleToggleHtmlMode = () => {
    if (isHtmlMode) {
      const sanitized = sanitizeRichTextHtml(htmlSource, { normalize: false });
      setHtmlSource(sanitized);
      onChangeRef.current(sanitized);
      editor.commands.setContent(sanitized, { emitUpdate: false });
      setIsHtmlMode(false);
      return;
    }

    const currentHtml = sanitizeRichTextHtml(editor.getHTML());
    setHtmlSource(currentHtml);
    onChangeRef.current(currentHtml);
    setIsHtmlMode(true);
  };

  return (
    <div className={cn(
      'w-full min-w-0 max-w-full rounded-lg overflow-hidden bg-card shadow-sm focus-within:ring-2 focus-within:ring-border-focus focus-within:shadow transition-all',
      disabled && 'opacity-50 pointer-events-none'
    )}>
      {/* Toolbar */}
      <div className="flex w-full min-w-0 max-w-full flex-wrap items-center gap-1 p-2 bg-muted">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleToggleHtmlMode}
          className={cn('h-8 px-2 gap-1.5 text-xs', isHtmlMode && 'bg-card')}
          title="Växla HTML-läge"
        >
          <Code2 className="h-3.5 w-3.5" />
          <span>HTML</span>
        </Button>

        {isHtmlMode && (
          <span className="ml-1 text-xs text-muted-foreground">Avancerat läge</span>
        )}

        {!isHtmlMode && (
          <>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onMouseDown={() => editor.chain().focus().toggleBold().run()}
          className={cn('h-8 w-8 p-0', editor.isActive('bold') && 'bg-card')}
          title="Fet"
        >
          <Bold className="h-4 w-4" />
        </Button>

        <Button
          type="button"
          variant="ghost"
          size="sm"
          onMouseDown={() => editor.chain().focus().toggleItalic().run()}
          className={cn('h-8 w-8 p-0', editor.isActive('italic') && 'bg-card')}
          title="Kursiv"
        >
          <Italic className="h-4 w-4" />
        </Button>

        <div className="w-px h-6 bg-border-subtle mx-1" />

        <Button
          type="button"
          variant="ghost"
          size="sm"
          onMouseDown={() => editor.chain().focus().toggleBulletList().run()}
          className={cn('h-8 w-8 p-0', editor.isActive('bulletList') && 'bg-card')}
          title="Punktlista"
        >
          <List className="h-4 w-4" />
        </Button>

        <Button
          type="button"
          variant="ghost"
          size="sm"
          onMouseDown={() => editor.chain().focus().toggleOrderedList().run()}
          className={cn('h-8 w-8 p-0', editor.isActive('orderedList') && 'bg-card')}
          title="Numrerad lista"
        >
          <ListOrdered className="h-4 w-4" />
        </Button>

        <div className="w-px h-6 bg-border-subtle mx-1" />

        {/* Link popover */}
        <Popover open={linkPopoverOpen} onOpenChange={setLinkPopoverOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleOpenLinkPopover}
              className={cn('h-8 w-8 p-0', editor.isActive('link') && 'bg-card')}
              title="Lägg till länk"
            >
              <LinkIcon className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[min(20rem,calc(100vw-2rem))] max-w-[calc(100vw-2rem)]" align="start">
            <div className="space-y-3">
              <div>
                <Label htmlFor="link-url">URL</Label>
                <Input
                  id="link-url"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  placeholder="https://example.com"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSetLink();
                    }
                  }}
                />
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="link-newtab"
                  checked={linkNewTab}
                  onCheckedChange={(checked) => setLinkNewTab(!!checked)}
                />
                <Label htmlFor="link-newtab" className="text-sm font-normal">
                  Öppna i ny flik
                </Label>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button type="button" size="sm" onClick={handleSetLink}>
                  {linkUrl ? 'Sätt länk' : 'Ta bort länk'}
                </Button>
                {editor.isActive('link') && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      editor.chain().focus().unsetLink().run();
                      setLinkPopoverOpen(false);
                    }}
                  >
                    <Unlink className="h-4 w-4 mr-1" />
                    Ta bort
                  </Button>
                )}
              </div>
            </div>
          </PopoverContent>
        </Popover>

        {/* Full-only toolbar items */}
        {!isSimple && (
          <>
            <div className="w-px h-6 bg-border-subtle mx-1" />

            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
              className={cn('h-8 w-8 p-0', editor.isActive('heading', { level: 2 }) && 'bg-muted')}
              title="Rubrik 2"
            >
              <Heading2 className="h-4 w-4" />
            </Button>

            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
              className={cn('h-8 w-8 p-0', editor.isActive('heading', { level: 3 }) && 'bg-muted')}
              title="Rubrik 3"
            >
              <Heading3 className="h-4 w-4" />
            </Button>

            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => editor.chain().focus().toggleBlockquote().run()}
              className={cn('h-8 w-8 p-0', editor.isActive('blockquote') && 'bg-muted')}
              title="Citat"
            >
              <Quote className="h-4 w-4" />
            </Button>

            {producerId && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleImageUpload}
                disabled={isUploading}
                className="h-8 w-8 p-0"
                title="Lägg till bild"
              >
                {isUploading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <ImageIcon className="h-4 w-4" />
                )}
              </Button>
            )}

            <div className="w-px h-6 bg-border-subtle mx-1" />

            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => editor.chain().focus().undo().run()}
              disabled={!editor.can().undo()}
              className="h-8 w-8 p-0"
              title="Ångra"
            >
              <Undo className="h-4 w-4" />
            </Button>

            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => editor.chain().focus().redo().run()}
              disabled={!editor.can().redo()}
              className="h-8 w-8 p-0"
              title="Gör om"
            >
              <Redo className="h-4 w-4" />
            </Button>
          </>
        )}

        {/* Expand button — always last in toolbar */}
        {expandable && (
          <>
            <div className="flex-1" />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleOpenWritingMode}
              className={cn(
                'h-8 px-2 gap-1 text-xs text-muted-foreground hover:text-foreground transition-all',
                showHint && 'animate-[writingHintPulse_2s_ease-in-out_3] text-primary/70',
              )}
              title="Öppna större skrivyta"
            >
              <Maximize2 className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Skrivläge</span>
            </Button>
          </>
        )}
          </>
        )}
      </div>

      {/* Editor content */}
      {isHtmlMode ? (
        <div className="space-y-2 p-3">
          <Textarea
            value={htmlSource}
            onChange={(event) => {
              const nextValue = event.target.value;
              setHtmlSource(nextValue);
              onChangeRef.current(nextValue);
            }}
            spellCheck={false}
            className="min-h-[240px] font-mono text-xs leading-6"
            aria-label="HTML-källa"
          />
          <p className="text-xs text-muted-foreground">
            Redigera HTML direkt. Innehållet saniteras när det renderas tillbaka i editorn.
          </p>
        </div>
      ) : (
        <EditorContent editor={editor} />
      )}

      {/* Expanded writing mode overlay */}
      {expandable && (
        <WritingSheet
          open={expandedOpen}
          onOpenChange={handleCloseWritingMode}
          title={expandedTitle}
          description={expandedDescription}
        >
          <RichTextEditor
            value={value}
            onChange={onChange}
            producerId={producerId}
            disabled={disabled}
            placeholder={placeholder}
            preset={preset}
            minHeightPx={350}
          />
        </WritingSheet>
      )}
    </div>
  );
}
